// Privacy tooling (docs/plans/golive-privacy-tooling.md). Internal IDs only in addresses;
// reasons and notes travel in bodies.
import { queryOptions } from '@tanstack/react-query';
import {
  auditChainStatusSchema,
  auditEventSchema,
  breakGlassEntrySchema,
  breakGlassPageSchema,
  labelSettingsSchema,
  pageSchema,
  retentionScheduleSchema,
  visitSensitivitySchema,
  type CreateLabelBody,
  type LabelChange,
  type RetentionCategory,
  type UpdateLabelBody,
  type VisitLabelChangeBody,
} from '@wodi/shared';

import { api } from './client.ts';

export const privacyKeys = {
  labels: ['privacy', 'labels'] as const,
  visit: (visitId: string) => ['privacy', 'visit', visitId] as const,
  breakGlass: (state: 'unreviewed' | 'all') => ['privacy', 'break-glass', state] as const,
  retention: ['privacy', 'retention'] as const,
};

// --- Labels -------------------------------------------------------------------------------------

export const labelSettingsQuery = queryOptions({
  queryKey: privacyKeys.labels,
  queryFn: () => api('GET', '/settings/sensitivity-labels', { schema: labelSettingsSchema }),
});

export const createLabel = (body: CreateLabelBody) =>
  api('POST', '/settings/sensitivity-labels', { body, schema: labelSettingsSchema });

export const updateLabel = (labelId: string, body: UpdateLabelBody) =>
  api('PUT', `/settings/sensitivity-labels/${labelId}`, { body, schema: labelSettingsSchema });

export const setLabelRoles = (labelId: string, roleIds: string[]) =>
  api('PUT', `/settings/sensitivity-labels/${labelId}/roles`, {
    body: { roleIds },
    schema: labelSettingsSchema,
  });

export const visitSensitivityQuery = (visitId: string) =>
  queryOptions({
    queryKey: privacyKeys.visit(visitId),
    queryFn: () => api('GET', `/visits/${visitId}/sensitivity`, { schema: visitSensitivitySchema }),
  });

export const changeVisitLabel = (
  visitId: string,
  change: LabelChange,
  body: VisitLabelChangeBody,
) =>
  api('POST', `/visits/${visitId}/sensitivity/${change}`, {
    body,
    schema: visitSensitivitySchema,
  });

// --- Break-glass report ------------------------------------------------------------------------

export const breakGlassPage = (state: 'unreviewed' | 'all', cursor?: string) =>
  api(
    'GET',
    `/break-glass-accesses?state=${state}${cursor === undefined ? '' : `&cursor=${cursor}`}`,
    { schema: breakGlassPageSchema },
  );

export const reviewBreakGlass = (auditEventId: string, note: string) =>
  api('POST', `/break-glass-accesses/${auditEventId}/review`, {
    body: { note },
    schema: breakGlassEntrySchema,
  });

// --- Audit viewer ------------------------------------------------------------------------------

export interface AuditFilters {
  actorId?: string;
  action?: string;
  entityId?: string;
  from?: string;
  to?: string;
}

const auditPageSchema = pageSchema(auditEventSchema);

export function auditEventsPage(filters: AuditFilters, cursor?: string) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters))
    if (typeof value === 'string' && value !== '') params.set(key, value);
  if (cursor !== undefined) params.set('cursor', cursor);
  const query = params.toString();
  return api('GET', `/audit-events${query === '' ? '' : `?${query}`}`, {
    schema: auditPageSchema,
  });
}

export const verifyAuditChain = () =>
  api('GET', '/audit-events/verify', { schema: auditChainStatusSchema });

// --- Retention ---------------------------------------------------------------------------------

export const retentionQuery = queryOptions({
  queryKey: privacyKeys.retention,
  queryFn: () => api('GET', '/settings/retention', { schema: retentionScheduleSchema }),
});

export const saveRetentionRule = (category: RetentionCategory, keepDays: number) =>
  api('PUT', `/settings/retention/${category}`, {
    body: { keepDays },
    schema: retentionScheduleSchema,
  });
